import React from 'react';
import firebase from '../../utilities/firebase';
import RequestManager from '../../utilities/requestManager';
import { applyTitleOdometerBrand } from '../../utilities/titleOdometer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

import Avatar from '@mui/material/Avatar';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArticleIcon from '@mui/icons-material/Article';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import PlaceIcon from '@mui/icons-material/Place';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import CreateLabelDialog from './CreateLabelDialog';
import DmvPacket from './DmvPacket';
import { StackedDialog, useSelectMenuProps } from '../../utilities/ModalStackContext';

import { StateManager } from '../../utilities/stateManager';
import SmsIcon from '@mui/icons-material/Sms';
import TitleTrackerPanel from './TitleTrackerPanel';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const BUCKET = 'skyway-dev-373d5.appspot.com';
const BLUE = '#023e84';
const GOLD = '#ca9e66';

// ─── Alternate shipping address ───────────────────────────────────────────────
// Lives on the car as `alt_shipping_address`. When it's complete, every title
// shipping feature (this tab's label dialog and the Title Processing page's
// batch shipping) ships there instead of the buyer's customer record.
const EMPTY_ALT_ADDRESS = {
  name: '', addressLine1: '', cityLocality: '', stateProvince: '', postalCode: '', countryCode: 'US',
};

const ALT_REQUIRED_FIELDS = ['addressLine1', 'cityLocality', 'stateProvince', 'postalCode'];

const normalizeAltAddress = (addr) => ({ ...EMPTY_ALT_ADDRESS, ...(addr || {}) });

const altAddressHasAnyValue = (addr) =>
  ['name', ...ALT_REQUIRED_FIELDS].some(k => !!(addr && String(addr[k] || '').trim()));

const altAddressIsComplete = (addr) =>
  ALT_REQUIRED_FIELDS.every(k => !!(addr && String(addr[k] || '').trim()));

// ─── Inline date picker ───────────────────────────────────────────────────────
function DMVDatePicker({ label, value, onChange, disabled }) {
  const parsed = value ? moment(value, 'YYYY/MM/DD').toDate() : null;
  return (
    <Box sx={{ position: 'relative' }}>
      <DatePicker
        selected={parsed}
        onChange={(date) => onChange(date ? moment(date).format('YYYY/MM/DD') : null)}
        disabled={disabled}
        dateFormat="MM/dd/yyyy"
        customInput={
          <TextField
            label={label}
            size="small"
            fullWidth
            InputProps={{ endAdornment: <CalendarMonthIcon sx={{ fontSize: 16, color: 'text.disabled', ml: 0.5 }} /> }}
            sx={{ '& .MuiInputBase-input': { cursor: 'pointer' } }}
          />
        }
        autoComplete="off"
        isClearable
        popperProps={{ strategy: 'fixed' }}
        popperPlacement="bottom-start"
      />
      <style>{`.react-datepicker-popper{z-index:9999 !important}`}</style>
    </Box>
  );
}

// ─── Inline toggle row ────────────────────────────────────────────────────────
function ToggleRow({ label, checked, onChange, disabled, accent }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        bgcolor: checked
          ? alpha(accent || theme.palette.success.main, 0.07)
          : alpha(theme.palette.grey[100], 0.6),
        border: '1px solid',
        borderColor: checked
          ? alpha(accent || theme.palette.success.main, 0.25)
          : 'transparent',
        transition: 'all 0.2s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {checked ? (
          <CheckCircleIcon sx={{ fontSize: 18, color: accent || 'success.main' }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        )}
        <Typography
          variant="body2"
          sx={{
            fontWeight: checked ? 600 : 400,
            color: checked ? (accent ? accent : 'success.dark') : 'text.secondary',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Switch
        checked={!!checked}
        onChange={(e) => !disabled && onChange(e.target.checked)}
        disabled={disabled}
        size="small"
        sx={{
          '& .MuiSwitch-switchBase.Mui-checked': { color: accent || undefined },
          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
            backgroundColor: accent || undefined,
          },
        }}
      />
    </Box>
  );
}

// ─── Alternate shipping address editor ───────────────────────────────────────
function AltShippingAddress({ value, buyerName, onChange, onClear }) {
  const address = normalizeAltAddress(value);
  const hasValue = altAddressHasAnyValue(address);
  // Derived rather than synced state, so clearing every field while typing
  // doesn't yank the form closed and switching cars can't leave it stuck open.
  const [manuallyOpened, setManuallyOpened] = React.useState(false);
  const expanded = hasValue || manuallyOpened;

  const complete = altAddressIsComplete(address);
  const setField = (key, val) => onChange({ ...address, [key]: val });

  const removeAddress = () => {
    setManuallyOpened(false);
    onClear();
  };

  if (!expanded) {
    return (
      <Button
        size="small"
        startIcon={<AddLocationAltIcon sx={{ fontSize: 17 }} />}
        onClick={() => setManuallyOpened(true)}
        sx={{ alignSelf: 'flex-start', fontSize: '0.78rem', color: BLUE, textTransform: 'none', '&:hover': { bgcolor: alpha(BLUE, 0.06) } }}
      >
        Add alternate shipping address
      </Button>
    );
  }

  return (
    <Box
      sx={{
        p: 2, borderRadius: 1.5, border: '1px solid',
        borderColor: complete ? alpha(BLUE, 0.35) : 'divider',
        bgcolor: complete ? alpha(BLUE, 0.03) : 'transparent',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PlaceIcon sx={{ fontSize: 18, color: complete ? BLUE : 'text.disabled' }} />
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, color: complete ? BLUE : 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Alternate Shipping Address
        </Typography>
        <Button
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
          onClick={removeAddress}
          sx={{ fontSize: '0.72rem', py: 0.2, px: 0.75, color: 'text.secondary', textTransform: 'none' }}
        >
          Remove
        </Button>
      </Box>

      <Stack spacing={1.5}>
        <TextField
          label="Recipient Name"
          size="small"
          fullWidth
          value={address.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={buyerName || ''}
          helperText={!address.name.trim() && buyerName ? `Defaults to the buyer, ${buyerName}` : ' '}
        />
        <TextField
          label="Street Address"
          size="small"
          fullWidth
          value={address.addressLine1}
          onChange={(e) => setField('addressLine1', e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <TextField
            label="City"
            size="small"
            sx={{ flex: 2 }}
            value={address.cityLocality}
            onChange={(e) => setField('cityLocality', e.target.value)}
          />
          <TextField
            label="State"
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ maxLength: 2 }}
            value={address.stateProvince}
            onChange={(e) => setField('stateProvince', e.target.value.toUpperCase())}
          />
          <TextField
            label="ZIP"
            size="small"
            sx={{ flex: 1 }}
            inputProps={{ maxLength: 10 }}
            value={address.postalCode}
            onChange={(e) => setField('postalCode', e.target.value)}
          />
        </Box>
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
        {complete ? (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: 'success.dark' }} />
            <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600 }}>
              Title shipping labels for this vehicle will use this address.
            </Typography>
          </>
        ) : (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 15, color: 'warning.dark' }} />
            <Typography variant="caption" sx={{ color: 'warning.dark' }}>
              Street, city, state and ZIP are all required — until then the buyer's address is used.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

// ─── Inline document upload slot ─────────────────────────────────────────────
// Uses a native <label htmlFor> + <input type="file"> instead of react-dropzone.
// react-dropzone opens the picker via programmatic inputRef.click(), which iOS
// Safari silently drops when it's not directly synchronous with a tap event.
// A <label> pointing to a real input ID is the only reliable iOS trigger.
function DocSlot({ label, fieldId, stockNumber, currentPath, onUpdate }) {
  const theme = useTheme();
  const [uploading, setUploading] = React.useState(false);
  const [path, setPath] = React.useState(currentPath || null);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const replaceRef = React.useRef(null);
  const inputId = `docslot-${fieldId}-${stockNumber}`;

  React.useEffect(() => { setPath(currentPath || null); }, [currentPath]);

  React.useEffect(() => {
    if (!path) { setPreviewUrl(null); return; }
    firebase.app().storage(BUCKET).ref(path).getDownloadURL()
      .then(url => setPreviewUrl(url))
      .catch(() => setPreviewUrl(null));
  }, [path]);

  const doUpload = async (file) => {
    setUploading(true);
    try {
      // Resolve extension — iOS camera often gives no extension or HEIC
      const rawExt = (file.name.includes('.') ? file.name.split('.').pop() : '').toLowerCase();
      let ext = rawExt;
      if (!ext) {
        if (file.type === 'application/pdf') ext = 'pdf';
        else ext = 'jpg'; // safe default for any image from iOS
      }

      let fullPath = `titles/${stockNumber}-${fieldId}.${ext}`;
      const ref = firebase.app().storage(BUCKET).ref(fullPath);
      StateManager.setAlertAndOpen('Upload started — you can keep working.', 'info');

      const result = await StateManager.uploadFile({ storageRef: ref, file });
      if (result) {
        // Convert HEIC/HEIF → JPEG so the browser can display it
        // RequestManager.post already returns the parsed body — do not call .json() on it.
        if (['heic', 'heif'].includes(ext)) {
          try {
            const data = await RequestManager.post({ function: 'convertHeicToJpeg', variables: { storagePath: fullPath } });
            if (data && data.success && data.jpegPath) fullPath = data.jpegPath;
          } catch {} // conversion failure is non-fatal — file is still saved
        }
        setPath(fullPath);
        await firebase.firestore().doc('cars/' + stockNumber).set({ [fieldId]: fullPath }, { merge: true });
        onUpdate?.(fieldId, fullPath);
      }
    } catch (err) {
      StateManager.setAlertAndOpen(`Upload failed: ${err.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const viewFile = async () => {
    if (!path) return;
    const url = await firebase.app().storage(BUCKET).ref(path).getDownloadURL();
    window.open(url, '_blank');
  };

  const deleteFile = async () => {
    if (!window.confirm(`Remove ${label}?`)) return;
    setUploading(true);
    try { await firebase.app().storage(BUCKET).ref(path).delete(); } catch {}
    await firebase.firestore().doc('cars/' + stockNumber).set({ [fieldId]: null }, { merge: true });
    onUpdate?.(fieldId, null);
    setPath(null);
    setPreviewUrl(null);
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading || !!path) return;
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  const pathLower = path?.toLowerCase() || '';
  const isImage = !!previewUrl && !['.pdf', '.heic', '.heif'].some(e => pathLower.endsWith(e));

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      {/* Hidden input — label click opens this natively on iOS and desktop */}
      <input
        id={inputId}
        data-sold-ok
        type="file"
        accept="image/*,application/pdf,.heic,.heif"
        style={{ display: 'none' }}
        disabled={uploading || !!path}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }}
      />
      {uploading ? (
        <Box sx={{ height: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Uploading…</Typography>
        </Box>
      ) : path ? (
        <Box sx={{ position: 'relative', height: 100, borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'success.light', bgcolor: isImage ? undefined : alpha(theme.palette.success.main, 0.06) }}>
          {isImage ? (
            <img src={previewUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
              <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'success.dark', fontWeight: 600 }}>On file</Typography>
            </Box>
          )}
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, opacity: 0, transition: 'opacity 0.15s', '&:hover': { opacity: 1 } }}>
            <Tooltip title="View"><IconButton size="small" onClick={viewFile} data-sold-ok sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}><OpenInNewIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
            <Tooltip title="Replace"><IconButton size="small" onClick={() => replaceRef.current?.click()} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}><EditIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" onClick={deleteFile} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}><CloseIcon sx={{ fontSize: 15 }} /></IconButton></Tooltip>
          </Box>
        </Box>
      ) : (
        <label htmlFor={inputId} data-sold-ok style={{ display: 'block', cursor: 'pointer' }}>
          <Box
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{ height: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.75, border: '2px dashed', borderColor: dragOver ? BLUE : 'divider', borderRadius: 1.5, bgcolor: dragOver ? alpha(BLUE, 0.04) : 'grey.50', transition: 'all 0.15s', '&:hover': { borderColor: BLUE, bgcolor: alpha(BLUE, 0.03) } }}
          >
            <ArticleIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {dragOver ? 'Drop here' : 'Upload'}
            </Typography>
          </Box>
        </label>
      )}
      <input ref={replaceRef} type="file" accept="image/*,application/pdf,.heic,.heif" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }} />
    </Box>
  );
}

// ─── Section card wrapper ─────────────────────────────────────────────────────
function SectionCard({ icon, title, complete, accent, children }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: complete ? alpha(accent || theme.palette.success.main, 0.35) : theme.palette.divider,
        borderRadius: 2,
        transition: 'border-color 0.3s',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2.5,
          py: 1.5,
          bgcolor: complete
            ? alpha(accent || theme.palette.success.main, 0.06)
            : alpha(BLUE, 0.03),
          borderBottom: '1px solid',
          borderColor: complete ? alpha(accent || theme.palette.success.main, 0.2) : theme.palette.divider,
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Box sx={{ color: complete ? (accent || theme.palette.success.main) : BLUE, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: complete ? (accent || theme.palette.success.dark) : BLUE, flex: 1 }}>
          {title}
        </Typography>
        {complete && (
          <Chip
            label="Complete"
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              bgcolor: alpha(accent || theme.palette.success.main, 0.12),
              color: accent || theme.palette.success.dark,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22,
              border: '1px solid',
              borderColor: alpha(accent || theme.palette.success.main, 0.3),
              '& .MuiChip-icon': { color: accent || theme.palette.success.dark },
            }}
          />
        )}
      </Box>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Paper>
  );
}

// ─── Progress stepper ─────────────────────────────────────────────────────────
function ProgressStepper({ steps }) {
  const theme = useTheme();
  const done = steps.filter(s => s.done).length;
  const pct = Math.round((done / steps.length) * 100);

  return (
    <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, mb: 2.5, background: `linear-gradient(135deg, ${alpha(BLUE, 0.03)} 0%, white 100%)` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BLUE }}>
          Titling Progress
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: pct === 100 ? 'success.main' : 'text.secondary' }}>
          {done}/{steps.length} steps · {pct}%
        </Typography>
      </Box>

      {/* Progress bar */}
      <Box sx={{ height: 6, bgcolor: alpha(BLUE, 0.1), borderRadius: 3, mb: 2, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: pct === 100 ? 'success.main' : BLUE, borderRadius: 3, transition: 'width 0.4s ease' }} />
      </Box>

      {/* Steps */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {steps.map((step, i) => (
          <Box key={i} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: step.done ? (step.gold ? GOLD : BLUE) : alpha(BLUE, 0.08), transition: 'all 0.3s' }}>
              {step.done ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
              ) : (
                <Typography variant="caption" sx={{ fontWeight: 700, color: alpha(BLUE, 0.4), fontSize: '0.65rem' }}>{i + 1}</Typography>
              )}
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: step.done ? 600 : 400, color: step.done ? (step.gold ? GOLD : BLUE) : 'text.disabled', textAlign: 'center', lineHeight: 1.2 }}>
              {step.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}



// ─── Avatar helpers ───────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#1976d2', '#388e3c', '#d32f2f', '#7b1fa2', '#f57c00', '#0097a7', '#c2185b', '#455a64'];
function avatarColor(name = '') {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}
function initials(name = '') {
  return name.trim().split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}
function fmtTs(ts) {
  const m = moment(ts, ['YYYY/MM/DD HH:mm', moment.ISO_8601]);
  if (!m.isValid()) return ts || '';
  const now = moment();
  if (now.diff(m, 'hours') < 24) return m.format('h:mm a');
  if (now.diff(m, 'days') < 7) return m.format('ddd h:mm a');
  return m.format('MMM D, h:mm a');
}

// ─── Main DMV component ───────────────────────────────────────────────────────
export default function DMV({ car = {} }) {
  const theme = useTheme();
  const menuProps = useSelectMenuProps();
  const stockNumber = car.stock;

  // Local car-field state (mirrors car prop, updated optimistically)
  const [fields, setFields] = React.useState({
    title_received: null,
    prev_state: '',
    title_front: null,
    title_back: null,
    title_ready_for_processing: false,
    title_taken: null,
    title_picked_up: null,
    title_sent: null,
    title_sent_method: '',
    title_sent_tracking: '',
    shipping_label: null,
    label_id: null,
    label_carrier_code: null,
    label_service_code: null,
    label_created_at: null,
    tracking_status: null,
    tracking_emailed_at: null,
    tracking_emailed_to: null,
  });
  const [altAddress, setAltAddress] = React.useState(() => normalizeAltAddress(car.alt_shipping_address));
  const [packet, setPacket] = React.useState({ files: [] });
  const [labelDialogOpen, setLabelDialogOpen] = React.useState(false);
  const [trackingEmailDialogOpen, setTrackingEmailDialogOpen] = React.useState(false);
  const [sendingTrackingEmail, setSendingTrackingEmail] = React.useState(false);
  const [trackingRecipient, setTrackingRecipient] = React.useState('');

  // Sync from car prop when it loads/updates
  React.useEffect(() => {
    setFields({
      title_received: car.title_received || null,
      prev_state: car.prev_state || '',
      title_front: car.title_front || null,
      title_back: car.title_back || null,
      needs_title_processing: !!car.needs_title_processing,
      title_ready_for_processing: !!car.title_ready_for_processing,
      title_taken: car.title_taken || null,
      title_picked_up: car.title_picked_up || null,
      title_sent: car.title_sent || null,
      title_sent_method: car.title_sent_method || '',
      title_sent_tracking: car.title_sent_tracking || '',
      shipping_label: car.shipping_label || null,
      label_id: car.label_id || null,
      label_carrier_code: car.label_carrier_code || null,
      label_service_code: car.label_service_code || null,
      label_created_at: car.label_created_at || null,
      tracking_status: car.tracking_status || null,
      tracking_emailed_at: car.tracking_emailed_at || null,
      tracking_emailed_to: car.tracking_emailed_to || null,
    });
    setAltAddress(normalizeAltAddress(car.alt_shipping_address));
  }, [car.stock]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load the DMV packet doc. DmvPacket owns the file list; this copy is here for
  // the notes feed below, and is refreshed when DmvPacket changes the packet.
  const reloadPacket = React.useCallback(() => {
    if (!stockNumber) return Promise.resolve();
    return firebase.firestore().doc('dmv_packet/' + stockNumber).get()
      .then(snap => { if (snap.exists) setPacket({ files: [], ...snap.data() }); })
      .catch(() => {});
  }, [stockNumber]);

  // Notes live on the packet document. Only the notes array is written here — the
  // documents themselves are DmvPacket's business.
  const updateNotes = (notes) => {
    setPacket(prev => ({ ...prev, notes }));
    firebase.firestore().doc('dmv_packet/' + stockNumber).set({ notes }, { merge: true })
      .catch(err => StateManager.setAlertAndOpen('Failed to save note: ' + err.message, 'error'));
  };

  React.useEffect(() => { reloadPacket(); }, [reloadPacket]);

  // ── Car field updater ───────────────────────────────────────────────────────
  const updateField = (id, value) => {
    setFields(prev => ({ ...prev, [id]: value }));
    firebase.firestore().doc('cars/' + stockNumber).set({ [id]: value }, { merge: true })
      .catch(err => StateManager.setAlertAndOpen('Failed to save: ' + err.message, 'error'));
    StateManager.updateCar({ [id]: value });

    // Deal status side-effects
    if (id === 'title_received') {
      const status = value ? 'processing' : 'pending';
      firebase.firestore().doc('deals/' + stockNumber).set({ title_status: status }, { merge: true });
    }
    if (id === 'title_sent') {
      const status = value ? 'complete' : 'processing';
      firebase.firestore().doc('deals/' + stockNumber).set({ title_status: status }, { merge: true });
    }
  };

  // ── Alternate shipping address ──────────────────────────────────────────────
  // Debounced so typing doesn't fire a write per keystroke, with a flush on
  // unmount so closing the modal mid-edit still saves what was typed.
  const altSaveTimer = React.useRef(null);
  const altPending = React.useRef(null);

  const flushAltAddress = React.useCallback(() => {
    clearTimeout(altSaveTimer.current);
    const pending = altPending.current;
    if (!pending) return;
    altPending.current = null;
    firebase.firestore().doc('cars/' + pending.stock).set({ alt_shipping_address: pending.value }, { merge: true })
      .catch(err => StateManager.setAlertAndOpen('Failed to save alternate address: ' + err.message, 'error'));
  }, []);

  React.useEffect(() => flushAltAddress, [flushAltAddress]);

  const updateAltAddress = (next) => {
    setAltAddress(next);
    StateManager.updateCar({ alt_shipping_address: next });
    altPending.current = { stock: stockNumber, value: next };
    clearTimeout(altSaveTimer.current);
    altSaveTimer.current = setTimeout(flushAltAddress, 700);
  };

  const clearAltAddress = () => {
    clearTimeout(altSaveTimer.current);
    altPending.current = null;
    setAltAddress(normalizeAltAddress(null));
    StateManager.updateCar({ alt_shipping_address: null });
    firebase.firestore().doc('cars/' + stockNumber)
      .set({ alt_shipping_address: firebase.firestore.FieldValue.delete() }, { merge: true })
      .catch(err => StateManager.setAlertAndOpen('Failed to clear alternate address: ' + err.message, 'error'));
  };

  // ── Label helpers ───────────────────────────────────────────────────────────
  const TRACKING_LABELS = { pending: 'Label Created', in_transit: 'In Transit', delivered: 'Delivered', exception: 'Exception' };
  const TRACKING_COLORS = { pending: '#757575', in_transit: BLUE, delivered: '#2e7d32', exception: '#d32f2f' };

  const viewLabel = async () => {
    if (!fields.shipping_label) return;
    try {
      const url = await firebase.app().storage(BUCKET).ref(fields.shipping_label).getDownloadURL();
      window.open(url, '_blank');
    } catch {
      StateManager.setAlertAndOpen('Could not open label PDF', 'error');
    }
  };

  const handleLabelCreated = (result) => {
    const update = {
      title_sent_tracking: result.trackingNumber,
      shipping_label: result.labelPath,
      label_id: result.labelId,
      label_carrier_code: result.carrierCode || null,
      label_service_code: result.serviceCode || null,
      label_created_at: result.labelCreatedAt || null,
      tracking_status: 'pending',
      title_sent: result.today,
      title_sent_method: 'mail',
    };
    setFields(prev => ({ ...prev, ...update }));
    StateManager.updateCar(update);
  };

  // ── Email tracking number to customer ─────────────────────────────────────────
  const openTrackingEmailDialog = () => {
    setTrackingRecipient(car.buyer?.email || '');
    setTrackingEmailDialogOpen(true);
  };

  const sendTrackingEmail = async () => {
    const recipient = trackingRecipient.trim();
    if (!recipient) return;
    setSendingTrackingEmail(true);
    try {
      const res = await RequestManager.post({
        function: 'sendTrackingEmail',
        variables: { stockNumber, sentById: StateManager.userID, recipient },
      });
      if (res.success === false || res.error) throw new Error(res.error || 'Send failed');
      const update = { tracking_emailed_at: res.emailedAt, tracking_emailed_to: res.recipient };
      setFields(prev => ({ ...prev, ...update }));
      StateManager.updateCar(update);
      StateManager.setAlertAndOpen('Tracking number sent to customer', 'success');
      setTrackingEmailDialogOpen(false);
    } catch (e) {
      StateManager.setAlertAndOpen(e.message || 'Error sending tracking number', 'error');
    } finally {
      setSendingTrackingEmail(false);
    }
  };

  // ── Notes ───────────────────────────────────────────────────────────────────
  const [noteDraft, setNoteDraft] = React.useState('');
  const [noteHover, setNoteHover] = React.useState(null);
  const me = StateManager.userName || 'Unknown';

  const addNote = async () => {
    const trimmed = noteDraft.trim();
    if (!trimmed) return;
    const note = { value: trimmed, timestamp: moment().format('YYYY/MM/DD HH:mm'), author: me };
    const updated = [...(packet.notes || []), note];
    setNoteDraft('');
    updateNotes(updated);
  };

  const deleteNote = async (index) => {
    if (!window.confirm('Delete this note?')) return;
    const updated = (packet.notes || []).filter((_, i) => i !== index);
    updateNotes(updated);
  };

  // ── Derived state ───────────────────────────────────────────────────────────
  const allTasksComplete = !!fields.title_sent;

  const steps = [
    { label: 'Title\nReceived', done: !!fields.title_received },
    { label: 'Documents\nUploaded', done: !!(fields.title_front && fields.title_back) },
    { label: 'Ready for\nProcessing', done: !!fields.title_ready_for_processing },
    { label: 'Picked Up\nfrom State', done: !!fields.title_picked_up },
    { label: 'Delivered\nto Customer', done: !!fields.title_sent, gold: true },
  ];

  const section1Complete = !!(fields.title_received && fields.title_front && fields.title_back);
  const section2Complete = !!(fields.title_ready_for_processing && fields.title_taken && fields.title_picked_up);
  const section4Complete = !!fields.title_sent;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <ProgressStepper steps={steps} />

      <Stack spacing={2.5}>
        {/* ── Section 1: Title Receipt ─────────────────────────────── */}
        <SectionCard
          icon={<ArticleIcon sx={{ fontSize: 20 }} />}
          title="Title Receipt"
          complete={section1Complete}
        >
          <Stack spacing={2}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={8}>
                <DMVDatePicker
                  label="Date Title Received"
                  value={fields.title_received}
                  onChange={(val) => updateField('title_received', val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                {/* The state that issued the incoming title. Same field as
                    "Previous title state" on the title application. */}
                <FormControl size="small" fullWidth>
                  <InputLabel>Title State</InputLabel>
                  <Select
                    value={fields.prev_state}
                    label="Title State"
                    MenuProps={menuProps}
                    onChange={(e) => updateField('prev_state', e.target.value)}
                  >
                    <MenuItem value="">— Select —</MenuItem>
                    {StateManager.states.map(s => (
                      <MenuItem key={s.value} value={s.value}>{s.value} — {s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DocSlot
                  label="Title Front"
                  fieldId="title_front"
                  stockNumber={stockNumber}
                  currentPath={fields.title_front}
                  onUpdate={(id, val) => {
                    setFields(prev => ({ ...prev, [id]: val }));
                    StateManager.updateCar({ [id]: val });
                    if (val) applyTitleOdometerBrand(stockNumber, val);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DocSlot
                  label="Title Back"
                  fieldId="title_back"
                  stockNumber={stockNumber}
                  currentPath={fields.title_back}
                  onUpdate={(id, val) => { setFields(prev => ({ ...prev, [id]: val })); StateManager.updateCar({ [id]: val }); }}
                />
              </Grid>
            </Grid>

          </Stack>
        </SectionCard>
        {/* ── The DMV Packet ───────────────────────────────────────── */}
        {/* Sits under Title Receipt: the title has to arrive before there is a
            packet to build. Shows what this deal needs, what we already hold,
            and what can be cut straight out of the signed sales packet. */}
        <DmvPacket car={car} onPacketChanged={reloadPacket} />

        {/* ── Section 2: Processing Pipeline ──────────────────────── */}
        <SectionCard
          icon={<InventoryIcon sx={{ fontSize: 20 }} />}
          title="Processing Pipeline"
          complete={section2Complete}
        >
          <Stack spacing={1.5}>
            <ToggleRow
              label="Queue for Pre-Sale Title Processing"
              checked={fields.needs_title_processing}
              onChange={(val) => updateField('needs_title_processing', val)}
              accent={GOLD}
            />

            <ToggleRow
              label="Ready for Processing"
              checked={fields.title_ready_for_processing}
              onChange={(val) => updateField('title_ready_for_processing', val)}
              accent={BLUE}
            />

            <DMVDatePicker
              label="Date Taken to State"
              value={fields.title_taken}
              onChange={(val) => updateField('title_taken', val)}
            />

            <DMVDatePicker
              label="Date Picked Up from State"
              value={fields.title_picked_up}
              onChange={(val) => updateField('title_picked_up', val)}
            />
          </Stack>
        </SectionCard>


        {/* ── Section 4: Customer Delivery ─────────────────────────── */}
        <SectionCard
          icon={<LocalShippingIcon sx={{ fontSize: 20 }} />}
          title="Customer Delivery"
          complete={section4Complete}
          accent={section4Complete ? GOLD : undefined}
        >
          <Stack spacing={2}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={5}>
                <DMVDatePicker
                  label="Date Sent to Customer"
                  value={fields.title_sent}
                  onChange={(val) => updateField('title_sent', val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Delivery Method</InputLabel>
                  <Select
                    value={fields.title_sent_method}
                    label="Delivery Method"
                    onChange={(e) => updateField('title_sent_method', e.target.value)}
                  >
                    <MenuItem value="">— Select —</MenuItem>
                    <MenuItem value="mail">📬 Mail</MenuItem>
                    <MenuItem value="email">📧 Email</MenuItem>
                    <MenuItem value="pickup">🏪 In-Person Pickup</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {fields.title_sent_method === 'mail' && (
                <Grid item xs={12}>
                  {fields.label_id ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Tracking:</Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                          {fields.title_sent_tracking}
                        </Typography>
                        <Tooltip title="Copy tracking number">
                          <IconButton size="small" data-sold-ok onClick={() => navigator.clipboard.writeText(fields.title_sent_tracking || '')}>
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                        onClick={viewLabel}
                        data-sold-ok
                        sx={{ fontSize: '0.75rem', py: 0.4, px: 1.25, borderColor: BLUE, color: BLUE, '&:hover': { borderColor: BLUE, bgcolor: alpha(BLUE, 0.04) } }}
                      >
                        View Label
                      </Button>
                      {fields.tracking_status && (
                        <Chip
                          label={TRACKING_LABELS[fields.tracking_status] || fields.tracking_status}
                          size="small"
                          sx={{
                            bgcolor: alpha(TRACKING_COLORS[fields.tracking_status] || '#757575', 0.1),
                            color: TRACKING_COLORS[fields.tracking_status] || '#757575',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            border: '1px solid',
                            borderColor: alpha(TRACKING_COLORS[fields.tracking_status] || '#757575', 0.3),
                          }}
                        />
                      )}

                      {/* Email tracking to customer */}
                      <Box sx={{ flexBasis: '100%', height: 0 }} />
                      {fields.tracking_emailed_at ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.dark' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'success.dark' }}>
                              Tracking emailed to customer
                              {fields.tracking_emailed_to ? ` (${fields.tracking_emailed_to})` : ''}
                              {` on ${moment(fields.tracking_emailed_at).format('MM/DD/YYYY')}`}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            onClick={openTrackingEmailDialog}
                            sx={{ fontSize: '0.7rem', py: 0.2, px: 1, color: BLUE }}
                          >
                            Resend
                          </Button>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
                          onClick={openTrackingEmailDialog}
                          sx={{ fontSize: '0.75rem', py: 0.4, px: 1.25, borderColor: GOLD, color: GOLD, '&:hover': { borderColor: GOLD, bgcolor: alpha(GOLD, 0.06) } }}
                        >
                          Email Tracking to Customer
                        </Button>
                      )}
                    </Box>
                  ) : (
                    <Button
                      variant="outlined"
                      startIcon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
                      onClick={() => setLabelDialogOpen(true)}
                      sx={{ fontWeight: 600, borderColor: BLUE, color: BLUE, '&:hover': { borderColor: BLUE, bgcolor: alpha(BLUE, 0.04) } }}
                    >
                      Create Shipping Label
                    </Button>
                  )}
                </Grid>
              )}
            </Grid>

            {/* Alternate shipping address — overrides the buyer's address for
                this tab's label dialog and the Title Processing batch shipping */}
            <AltShippingAddress
              key={stockNumber}
              value={altAddress}
              buyerName={(car.buyer || {}).display_name}
              onChange={updateAltAddress}
              onClear={clearAltAddress}
            />

            {/* All Tasks Complete — auto-derived */}
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderRadius: 1.5,
                bgcolor: allTasksComplete ? alpha(GOLD, 0.08) : alpha(theme.palette.grey[100], 0.7),
                border: '1px solid',
                borderColor: allTasksComplete ? alpha(GOLD, 0.4) : 'divider',
                transition: 'all 0.3s',
              }}
            >
              {allTasksComplete ? (
                <VerifiedIcon sx={{ fontSize: 22, color: GOLD }} />
              ) : (
                <RadioButtonUncheckedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: allTasksComplete ? GOLD : 'text.disabled' }}>
                  All Tasks Complete
                </Typography>
                <Typography variant="caption" sx={{ color: allTasksComplete ? alpha(GOLD, 0.8) : 'text.disabled' }}>
                  {allTasksComplete
                    ? `Title sent on ${fields.title_sent}`
                    : 'Automatically marked complete when title is sent to customer'}
                </Typography>
              </Box>
              {allTasksComplete && <StarIcon sx={{ fontSize: 18, color: GOLD }} />}
            </Box>
          </Stack>
        </SectionCard>

        {/* ── Customer Title Tracker ───────────────────────────────── */}
        <SectionCard
          icon={<SmsIcon sx={{ fontSize: 20 }} />}
          title="Customer Title Tracker"
        >
          <TitleTrackerPanel stockNumber={stockNumber} hasBuyer={!!car.buyer} />
        </SectionCard>

        {/* ── Section 5: Notes ─────────────────────────────────────── */}
        <SectionCard
          icon={<NoteAltIcon sx={{ fontSize: 20 }} />}
          title="DMV Notes"
          complete={false}
        >
          <Box>
            {/* Feed */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2, minHeight: 40 }}>
              {(packet.notes || []).length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', py: 1 }}>
                  No notes yet — add the first one below.
                </Typography>
              ) : (
                (packet.notes || []).map((note, i) => {
                  const author = note.author || 'Unknown';
                  const isMe = author === me;
                  const color = avatarColor(author);
                  return (
                    <Box
                      key={i}
                      onMouseEnter={() => setNoteHover(i)}
                      onMouseLeave={() => setNoteHover(null)}
                      sx={{ display: 'flex', gap: 1.25, flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-start' }}
                    >
                      <Tooltip title={author} placement={isMe ? 'left' : 'right'}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: color, flexShrink: 0, mt: 0.25 }}>
                          {initials(author)}
                        </Avatar>
                      </Tooltip>
                      <Box sx={{ maxWidth: '72%', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.3, flexDirection: isMe ? 'row-reverse' : 'row' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
                            {isMe ? 'You' : author}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11, lineHeight: 1 }}>
                            {fmtTs(note.timestamp)}
                          </Typography>
                        </Box>
                        <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
                          <Paper
                            elevation={0}
                            sx={{
                              px: 1.75, py: 1.1,
                              bgcolor: isMe ? color : 'grey.100',
                              color: isMe ? 'white' : 'text.primary',
                              borderRadius: isMe ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
                              display: 'inline-block',
                              maxWidth: '100%',
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, wordBreak: 'break-word' }}>
                              {note.value}
                            </Typography>
                          </Paper>
                          {(StateManager.isAdmin() || StateManager.isManager()) && noteHover === i && (
                            <IconButton
                              size="small"
                              onClick={() => deleteNote(i)}
                              sx={{
                                position: 'absolute', top: -8, [isMe ? 'left' : 'right']: -8,
                                width: 22, height: 22, bgcolor: 'background.paper', boxShadow: 2,
                                color: 'text.secondary',
                                '&:hover': { color: 'error.main', bgcolor: (t) => alpha(t.palette.error.main, 0.08) },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Compose */}
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-end', pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Avatar sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: avatarColor(me), flexShrink: 0, mb: 0.25 }}>
                {initials(me)}
              </Avatar>
              <TextField
                fullWidth
                multiline
                maxRows={5}
                size="small"
                placeholder="Add a DMV note… (⌘↵ to send)"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addNote(); }}
              />
              <IconButton
                onClick={addNote}
                disabled={!noteDraft.trim()}
                sx={{ flexShrink: 0, mb: 0.25, color: BLUE, '&:hover': { bgcolor: alpha(BLUE, 0.08) }, '&.Mui-disabled': { color: 'text.disabled' } }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </Box>
        </SectionCard>
      </Stack>

      <CreateLabelDialog
        open={labelDialogOpen}
        onClose={() => setLabelDialogOpen(false)}
        stockNumber={stockNumber}
        altAddress={altAddressIsComplete(altAddress) ? altAddress : null}
        onLabelCreated={handleLabelCreated}
      />

      <StackedDialog
        open={trackingEmailDialogOpen}
        onClose={() => !sendingTrackingEmail && setTrackingEmailDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Email Tracking Number to Customer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will email the tracking number to the customer from
              {' '}<strong>sales_team@skywayclassics.com</strong>, CC'ing the deal's sales rep.
            </Typography>
            <TextField
              label="Recipient email"
              size="small"
              fullWidth
              value={trackingRecipient}
              onChange={(e) => setTrackingRecipient(e.target.value)}
              error={!trackingRecipient.trim()}
              helperText={!trackingRecipient.trim() ? 'No buyer email on file — enter a recipient.' : ' '}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Tracking:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                {fields.title_sent_tracking}
              </Typography>
              {fields.label_carrier_code && (
                <Typography variant="caption" color="text.secondary">
                  ({String(fields.label_carrier_code).toUpperCase()})
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingEmailDialogOpen(false)} disabled={sendingTrackingEmail}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={sendingTrackingEmail ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
            onClick={sendTrackingEmail}
            disabled={sendingTrackingEmail || !trackingRecipient.trim()}
          >
            {sendingTrackingEmail ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </StackedDialog>
    </Box>
  );
}
